import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Refresh, Settings, VpnKey, WhatsApp } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DropsContext } from 'contexts/DropsContext';
import Container from 'components/Container';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import Dropdown from 'components/Dropdown';
import {
  deleteWhatsappNumber,
  getWhatsappAccounts,
  getWhatsappOptions,
  getWhatsappPreferences,
  postWhatsapp,
  saveWhatsappPreferences,
} from 'services/whatsapp';
import { loadFacebookSDK, startEmbeddedSignup } from './embeddedSignup';

const errorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  'Não foi possível concluir a operação.';

const Whatsapp = ({ integracao }) => {
  const id = integracao.id;
  const { drops } = useContext(DropsContext);
  const { control, reset, handleSubmit } = useForm({
    defaultValues: {
      nome_integracao: '',
      user_notification: null,
      enviar_mensagens: false,
    },
  });
  const [tab, setTab] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waitingMeta, setWaitingMeta] = useState(false);
  const [preparing, setPreparing] = useState(true);
  const [signup, setSignup] = useState(null);
  const [sdkError, setSdkError] = useState('');
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [method, setMethod] = useState('SMS');
  const mounted = useRef(false);
  const locked = useRef(false);
  const cancelSignup = useRef(null);

  const refreshAccounts = useCallback(async () => {
    const data = await getWhatsappAccounts(id);
    if (mounted.current) setAccounts(data);
  }, [id]);

  const prepareSignup = useCallback(async () => {
    setPreparing(true);
    setSdkError('');
    try {
      const options = await getWhatsappOptions();
      const sdk = await loadFacebookSDK(options);
      if (mounted.current) setSignup({ options, sdk });
    } catch (error) {
      if (mounted.current) setSdkError(errorMessage(error));
    } finally {
      if (mounted.current) setPreparing(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    prepareSignup();
    return () => {
      mounted.current = false;
      cancelSignup.current?.();
    };
  }, [prepareSignup]);

  const load = useCallback(async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const [data, preferences] = await Promise.all([
        getWhatsappAccounts(id),
        getWhatsappPreferences(id),
      ]);
      if (!mounted.current) return;
      setAccounts(data);
      reset(preferences);
      setLoaded(true);
    } catch (error) {
      if (mounted.current)
        setNotice({ severity: 'error', text: errorMessage(error) });
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [id, reset]);

  useEffect(() => {
    load();
  }, [load]);

  const perform = async (
    operation,
    success,
    refresh = true,
    closeDialog = true
  ) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setNotice(null);
    try {
      await operation();
      if (!mounted.current) return;
      if (closeDialog) setDialog(null);
      setCode('');
      setPin('');
      setNotice({ severity: 'success', text: success });
      if (refresh) {
        try {
          await refreshAccounts();
        } catch {
          if (mounted.current)
            setNotice({
              severity: 'warning',
              text: `${success} Não foi possível atualizar a lista; clique em Atualizar.`,
            });
        }
      }
    } catch (error) {
      if (mounted.current) {
        setNotice({ severity: 'error', text: errorMessage(error) });
        // Login can save credentials before a subscription failure.
        if (refresh) await refreshAccounts().catch(() => {});
      }
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const connect = () => {
    if (!signup || locked.current) return;
    locked.current = true;
    setBusy(true);
    setWaitingMeta(true);
    setNotice({
      severity: 'info',
      text: 'Conclua a configuração na janela da Meta.',
    });
    cancelSignup.current = startEmbeddedSignup({
      ...signup,
      onStatus: (text) => {
        if (mounted.current) setNotice({ severity: 'info', text });
      },
      onError: (text) => {
        locked.current = false;
        if (!mounted.current) return;
        setBusy(false);
        setWaitingMeta(false);
        setNotice({ severity: 'warning', text });
      },
      onSubmit: (payload) => {
        locked.current = false;
        if (!mounted.current) return;
        setWaitingMeta(false);
        perform(
          () => postWhatsapp('Login', id, payload),
          'Conta conectada ao WhatsApp.'
        );
      },
    });
  };

  const cancelWaiting = () => {
    cancelSignup.current?.();
    cancelSignup.current = null;
    locked.current = false;
    setBusy(false);
    setWaitingMeta(false);
    setNotice({
      severity: 'warning',
      text: 'Espera cancelada. Feche qualquer janela anterior da Meta antes de tentar novamente.',
    });
  };

  const openPhone = (action, business, waba, phone) => {
    setCode('');
    setPin('');
    setMethod('SMS');
    setNotice(null);
    setDialog({ action, business, waba, phone });
  };

  const phoneAction = (action, values = {}) =>
    perform(
      () =>
        postWhatsapp(action, id, {
          phone_number_id: dialog.phone.phone_number_id,
          ...values,
        }),
      action === 'Register'
        ? 'Número registrado na Cloud API.'
        : values.code
          ? 'Número verificado.'
          : 'Código enviado. Informe o código recebido para verificar o número.',
      true,
      action !== 'Verify' || Boolean(values.code)
    );

  const titles = {
    verify: 'Verificar número',
    register: 'Registrar na Cloud API',
    disable: 'Desativar número',
    reconnect: 'Concluir conexão',
  };

  return (
    <Container>
      <Header titulo="Configurações" />
      <Grid container spacing={3} pb={4}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <WhatsApp sx={{ color: '#25D366', fontSize: 40 }} />
              <Typography variant="h5">WhatsApp</Typography>
            </Box>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab
                value={0}
                icon={<VpnKey />}
                iconPosition="start"
                label="Acesso"
              />
              <Tab
                value={1}
                icon={<Settings />}
                iconPosition="start"
                label="Configurações"
              />
            </Tabs>
          </Box>
        </Grid>
        {notice && (
          <Grid item xs={12}>
            <Alert severity={notice.severity} role="status">
              {notice.text}
            </Alert>
          </Grid>
        )}
        {!loaded && (
          <Grid item xs={12}>
            <Button
              onClick={load}
              loading={busy}
              disabled={busy}
              startIcon={<Refresh />}
            >
              Carregar configurações
            </Button>
          </Grid>
        )}
        <Grid item xs={12} display={tab === 0 ? 'block' : 'none'}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info">
              Conecte sua conta comercial e selecione o número de WhatsApp na
              janela da Meta. Após a conexão, os números vinculados aparecerão
              abaixo.
            </Alert>
            {sdkError && (
              <Alert
                severity="error"
                action={
                  <Button disabled={preparing} onClick={prepareSignup}>
                    Tentar novamente
                  </Button>
                }
              >
                <Box sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                  {sdkError}
                </Box>
              </Alert>
            )}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<WhatsApp />}
                disabled={
                  !loaded || busy || preparing || !signup || Boolean(sdkError)
                }
                loading={preparing}
                onClick={connect}
              >
                Conectar com a Meta
              </Button>
              <Button
                startIcon={<Refresh />}
                disabled={busy || !loaded}
                onClick={() =>
                  perform(() => refreshAccounts(), 'Lista atualizada.', false)
                }
              >
                Atualizar números
              </Button>
              {waitingMeta && (
                <Button onClick={cancelWaiting} color="secondary">
                  Cancelar espera
                </Button>
              )}
            </Box>
            {busy && (
              <Typography color="text.secondary">
                {waitingMeta
                  ? 'Aguardando a janela de autorização da Meta. Se ela não abriu, verifique se o navegador bloqueou o popup.'
                  : 'Processando solicitação na Esolution API...'}
              </Typography>
            )}
            {loaded && !accounts.length && (
              <Alert severity="info">
                Nenhuma conta conectada nesta integração.
              </Alert>
            )}
            {accounts.map((business, businessIndex) => (
              <Box
                key={business.business_id || businessIndex}
                border={1}
                borderColor="divider"
                borderRadius={2}
                p={2}
              >
                <Typography variant="h6">
                  {business.name || 'Conta comercial'}
                </Typography>
                {business.business_id && (
                  <Typography color="text.secondary">
                    ID: {business.business_id}
                  </Typography>
                )}
                {(business.wabas || []).map((waba) => (
                  <Box key={waba.waba_id} mt={2}>
                    <Typography fontWeight="bold">
                      {waba.name || 'Conta do WhatsApp'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      WABA: {waba.waba_id}
                    </Typography>
                    {(waba.phones || []).map((item) => {
                      const phone =
                        typeof item === 'object'
                          ? item
                          : { phone_number_id: String(item) };
                      return (
                        <Box
                          key={phone.phone_number_id}
                          mt={2}
                          p={2}
                          bgcolor="action.hover"
                          borderRadius={1}
                        >
                          <Typography fontWeight="bold">
                            {phone.phone || phone.phone_number_id}{' '}
                            {phone.verified_name && `— ${phone.verified_name}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID do número: {phone.phone_number_id}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} my={1}>
                            {phone.coexistencia && (
                              <Chip
                                size="small"
                                label="Coexistência: celular e ERP"
                                color="info"
                              />
                            )}
                            {phone.api_connected === false && (
                              <Chip
                                size="small"
                                label="Desconectado da Meta"
                                color="error"
                              />
                            )}
                            {phone.coexistence?.sync?.history?.status && (
                              <Chip
                                size="small"
                                label={`Histórico: ${{ requesting: 'solicitando', requested: 'aguardando dados', receiving: 'sincronizando', completed: 'concluído', declined: 'não compartilhado', failed: 'falha', expired: 'prazo encerrado', unknown: 'confirmação pendente' }[phone.coexistence.sync.history.status] || 'aguardando'}`}
                              />
                            )}

                            <Chip
                              size="small"
                              label={
                                phone.ativo === 'NAO'
                                  ? 'Envio desativado'
                                  : 'Envio habilitado'
                              }
                              color={
                                phone.ativo === 'NAO' ? 'default' : 'success'
                              }
                            />
                            <Chip
                              size="small"
                              label={
                                phone.code_verification_status === 'VERIFIED'
                                  ? 'Número verificado'
                                  : 'Verificação não confirmada'
                              }
                            />
                            <Chip
                              size="small"
                              label={
                                phone.registered
                                  ? 'Registro confirmado'
                                  : 'Registro não confirmado pelo ERP'
                              }
                            />
                          </Box>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                              disabled={busy}
                              onClick={() =>
                                openPhone('reconnect', business, waba, phone)
                              }
                            >
                              Concluir conexão / Reativar
                            </Button>
                            <Button
                              disabled={
                                busy ||
                                phone.coexistencia === true ||
                                phone.is_on_biz_app === true
                              }
                              onClick={() =>
                                openPhone('verify', business, waba, phone)
                              }
                            >
                              Verificar
                            </Button>
                            <Button
                              disabled={
                                busy ||
                                phone.coexistencia === true ||
                                phone.is_on_biz_app === true
                              }
                              onClick={() =>
                                openPhone('register', business, waba, phone)
                              }
                            >
                              Registrar
                            </Button>
                            <Button
                              disabled={busy || phone.ativo === 'NAO'}
                              color="error"
                              onClick={() =>
                                openPhone('disable', business, waba, phone)
                              }
                            >
                              Desativar
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} display={tab === 1 ? 'block' : 'none'}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Input
                name="nome_integracao"
                label="Nome da Integração"
                control={control}
                disabled={busy || !loaded}
              />
            </Grid>
            <Grid item xs={12}>
              <Dropdown
                name="user_notification"
                label="Usuário para receber notificações"
                options={drops?.Users}
                control={control}
                disabled={busy || !loaded}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="enviar_mensagens"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Habilitar envio de mensagens pelos processos do ERP"
                    control={
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => field.onChange(checked)}
                        disabled={busy || !loaded}
                      />
                    }
                  />
                )}
              />
              <Alert severity="info">
                Esta opção habilita o envio pelos processos do ERP. Ela não cria
                mensagens automáticas para os eventos dos documentos.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                disabled={busy || !loaded}
                loading={busy}
                onClick={handleSubmit((values) =>
                  perform(
                    () => saveWhatsappPreferences(id, values),
                    'Configurações salvas.',
                    false
                  )
                )}
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={Boolean(dialog)}
        onClose={() => {
          if (!busy) {
            setDialog(null);
            setPin('');
            setCode('');
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{titles[dialog?.action]}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Typography>
              {dialog?.phone.phone || dialog?.phone.phone_number_id}
            </Typography>
            {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
            {dialog?.action === 'verify' && (
              <>
                <TextField
                  select
                  label="Receber código por"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  disabled={busy}
                >
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="VOICE">Ligação</MenuItem>
                </TextField>
                <Button
                  disabled={busy}
                  onClick={() => phoneAction('Verify', { code_method: method })}
                >
                  Solicitar código
                </Button>
                <TextField
                  label="Código recebido"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric' }}
                  disabled={busy}
                />
              </>
            )}
            {dialog?.action === 'register' && (
              <>
                <Alert severity="info">
                  Informe o PIN de seis dígitos da verificação em duas etapas.
                  Ele é diferente do código recebido por SMS. Números já
                  registrados não precisam repetir esta etapa.
                </Alert>
                <TextField
                  label="PIN de seis dígitos"
                  type="password"
                  value={pin}
                  onChange={(e) =>
                    setPin(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  autoComplete="new-password"
                  disabled={busy}
                />
              </>
            )}
            {dialog?.action === 'disable' && (
              <Alert severity="warning">
                O envio por este número será desativado no ERP. O histórico e o
                recebimento de atualizações serão preservados.
              </Alert>
            )}
            {dialog?.action === 'reconnect' && (
              <Alert severity="info">
                Vamos concluir a conexão usando a autorização já salva e
                habilitar o envio. Se a autorização expirou, use Conectar com a
                Meta.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setDialog(null);
              setPin('');
              setCode('');
            }}
          >
            Cancelar
          </Button>
          {dialog?.action === 'verify' && (
            <Button
              variant="contained"
              disabled={busy || !code}
              onClick={() => phoneAction('Verify', { code })}
            >
              Verificar código
            </Button>
          )}
          {dialog?.action === 'register' && (
            <Button
              variant="contained"
              disabled={busy || !/^\d{6}$/.test(pin)}
              onClick={() => phoneAction('Register', { pin })}
            >
              Registrar
            </Button>
          )}
          {dialog?.action === 'disable' && (
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              onClick={() =>
                perform(
                  () => deleteWhatsappNumber(id, dialog.phone.phone_number_id),
                  'Envio desativado para este número.'
                )
              }
            >
              Desativar
            </Button>
          )}
          {dialog?.action === 'reconnect' && (
            <Button
              variant="contained"
              disabled={busy}
              onClick={() =>
                perform(
                  () =>
                    postWhatsapp('Login', id, {
                      ...(dialog.business.business_id
                        ? { business_id: dialog.business.business_id }
                        : {}),
                      waba_id: dialog.waba.waba_id,
                      phone_number_id: dialog.phone.phone_number_id,
                    }),
                  'Conexão concluída e envio habilitado.'
                )
              }
            >
              Concluir conexão
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Whatsapp;
